import React, { useEffect, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Empty,
  Flex,
  Modal,
  Table,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import { DraggableIcon } from '../../Icons';

import './TableCustomizer.css';

import { SettingsOutlined } from '@duxton-web/icons';

import { DashboardColumn, DashboardColumnSetting } from '.';

interface OwnProps {
  testId?: string;
  title?: string;
  open?: boolean;
  columns?: DashboardColumn[];
  // eslint-disable-next-line no-empty-pattern
  onOk?: ({}: Map<string, DashboardColumnSetting>) => void;
  onRestoreDefault?: () => void;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row = (props: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

function Columns({
  dataSource,
  selectedRows,
  columns,
  onDragEnd,
  onRowSelectionChange,
}) {
  if (!columns?.length) return <Empty data-testid="tableCustomizer-empty" />;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const rowSelection = {
    onChange: onRowSelectionChange,
    getCheckboxProps: (record: DashboardColumn) => ({
      name: record?.title?.toString() || '',
      disabled: record.fixed ? true : false,
    }),
    selectedRowKeys: selectedRows,
    renderCell: function (_checked, _record, _index, originNode) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <DraggableIcon />
          {originNode}
        </div>
      );
    },
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={dataSource.map((i) => i.key)}
        strategy={verticalListSortingStrategy}
      >
        <Table
          size="middle"
          data-testid="tableCustomizer-columns"
          className="grab-x__table-customizer"
          rowKey="key"
          columns={[
            {
              title: 'Column',
              render: (_: string, record: DashboardColumn) => {
                return (
                  <Flex vertical>
                    <Tooltip
                      title={
                        typeof record.title === 'string' ? record.title : ''
                      }
                    >
                      <Typography.Text>
                        {typeof record.title === 'function' ? '' : record.title}
                      </Typography.Text>
                    </Tooltip>
                    <Tooltip title={record.description}>
                      <Typography.Metadata type="secondary">
                        {record.description}
                      </Typography.Metadata>
                    </Tooltip>
                  </Flex>
                );
              },
            },
          ]}
          showHeader={false}
          components={{
            body: {
              row: Row,
            },
          }}
          pagination={{
            pageSize: columns?.length,
            hideOnSinglePage: true,
          }}
          scroll={{
            y: 400,
          }}
          dataSource={dataSource.filter((data) => !data.fixed)}
          rowSelection={{
            columnWidth: '48px',
            type: 'checkbox',
            ...rowSelection,
          }}
        />
      </SortableContext>
    </DndContext>
  );
}

function TableCustomizerButton({ onClick }) {
  return (
    <Tooltip title="Click here to customize table look and feel">
      <Button
        type="secondary"
        size="small"
        data-testid="tableCustomizer-btn"
        shape="round"
        onClick={onClick}
        icon={<SettingsOutlined />}
      >
        Edit Table Columns
      </Button>
    </Tooltip>
  );
}

function TableCustomizerModal({
  testId,
  title,
  open,
  columns,
  onCancel,
  onOk,
  onRestoreDefault,
}) {
  const [dataSource, setDataSource] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const getSelectedRows = (nonFixedColumns) => {
    let keys = [];

    if (!nonFixedColumns?.length) {
      setSelectedRows(keys);

      return;
    }

    nonFixedColumns.forEach((column) => {
      if (!column?.checked) return;

      keys.push(column.key);
    });

    return keys;
  };

  useEffect(() => {
    const nonFixedColumns = columns.filter((column) => !column?.fixed);

    setDataSource(nonFixedColumns);
    setSelectedRows(getSelectedRows(nonFixedColumns));
  }, [columns]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id);
        const overIndex = prev.findIndex((i) => i.key === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };

  function applyCustomization() {
    const tableSettingsMap = new Map();

    dataSource.forEach((column, index: number) => {
      tableSettingsMap.set(column.key, {
        index,
        visible: column.disabled || selectedRows.includes(column.key),
      });
    });

    onOk(tableSettingsMap);
  }

  if (!open) return <></>;

  return (
    <Modal
      data-testid={testId || 'tableCustomizer-modal'}
      className="grab-x__table-customizer-modal"
      title={title || 'Customize Table'}
      open={open}
      centered
      styles={{
        header: {
          padding: 16,
          borderBottom: '1px solid rgba(5, 5, 5, 0.06)',
        },

        content: {
          padding: 0,
        },
        body: {
          padding: 0,
        },
        footer: {
          padding: 16,
          borderTop: '1px solid rgba(5, 5, 5, 0.06)',
        },
      }}
      footer={
        <Flex align="center" justify="space-between">
          <Button size="small" onClick={onRestoreDefault}>
            Restore Default
          </Button>
          <Flex
            align="center"
            gap={8}
            style={{
              width: 'fit-content',
            }}
          >
            <Button size="small" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="small" type="primary" onClick={applyCustomization}>
              Apply
            </Button>
          </Flex>
        </Flex>
      }
      onCancel={onCancel}
    >
      <Columns
        dataSource={dataSource}
        selectedRows={selectedRows}
        columns={columns}
        onDragEnd={onDragEnd}
        onRowSelectionChange={(selectedRowKeys: React.Key[]) => {
          setSelectedRows(selectedRowKeys);
        }}
      />
    </Modal>
  );
}

export default function TableCustomizer({
  title,
  testId = '',
  columns = [],
  onOk,

  onRestoreDefault,
}: OwnProps) {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <TableCustomizerButton
        onClick={() => {
          setOpen(!open);
        }}
      />
      <TableCustomizerModal
        testId={testId}
        title={title}
        columns={columns}
        open={open}
        onOk={(tableSettingsMap) => {
          setOpen(!open);

          onOk(tableSettingsMap);
        }}
        onCancel={() => {
          setOpen(!open);
        }}
        onRestoreDefault={() => {
          setOpen(!open);

          onRestoreDefault();
        }}
      />
    </>
  );
}
